import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useCurrentUserProfile } from './CurrentUserProfileContext';
import { useFindGroups } from '../hooks/queries/useFindGroups';
import { useLiveRsvpEvent } from '../hooks/queries/useLiveRsvpEvent';
import { useFindSelectedGroup } from '../hooks/useFindSelectedGroup';
import { useProximity } from '../hooks/useProximity';
import { registerFindRemoteLogLifecycle } from '../lib/findRemoteLog/lifecycle';
import { getFindLiveUntil } from '../utils/eventCutoff';
import { getEventStartMs, pickCurrentLiveEvent } from '../lib/proximity/data/eventStart';
import {
  ensureFindRemoteLogPageContext,
  shutdownFindRemoteLogPageContext,
} from '../lib/findRemoteLog';

const FindProximityContext = createContext(null);

/** Routes that hold the proximity session in the foreground.
 *
 * The Maps page belongs here as much as Find does: it draws your own puck from
 * the same session. While it was excluded, opening /map paused BLE immediately
 * and the nav-grace timer tore the whole session down 120 s later — so your
 * position on the map went stale for no reason the user could see. */
function isProximityRoute(pathname) {
  return (
    pathname === '/find' ||
    pathname.startsWith('/find/') ||
    pathname === '/map' ||
    pathname.startsWith('/map/')
  );
}

/**
 * Whether the group's event is on now: from 12 h before its (first) date until Find's tail past the end has
 * run - events.ends_at (6 AM after its last day) plus 6 h for a show or 12 h for a festival (getFindLiveUntil;
 * Sam, 2026-10-04: people still need each other for the exit, the pickup and the car, or until they pack up
 * camp). Outside it, location runs only on Find and the map, and never for a group whose event is over.
 */
// 3 h before the first start (migration 147, event_presence_before_hours; Sam 2026-10-12 - was 12 h).
const LIVE_BEFORE_MS = 3 * 3600 * 1000;
/** event_presence_radius_m (migration 147): where a position counts as "there". */
const PRESENCE_RADIUS_M = 5000;
function isEventLiveNow(group, now = Date.now()) {
  // eventStartsAt, else eventDate read in the event's zone: a date-only eventDate parsed as UTC midnight opened the
  // window the afternoon before on the west coast (2026-10-16 audit).
  const start = getEventStartMs(group);
  if (!Number.isFinite(start)) return false;
  const until = getFindLiveUntil({
    ends_at: group.eventEndsAt,
    event_date: group.eventDate,
    is_festival: group.eventIsFestival,
  });
  return now >= start - LIVE_BEFORE_MS && (until == null || now < until.getTime());
}

export function FindProximityProvider({ children }) {
  const { pathname } = useLocation();
  const findPageActive = isProximityRoute(pathname);
  const { session, authReady, profile } = useCurrentUserProfile();
  const userId = session?.user?.id;

  const {
    data: groups = [],
    isLoading: groupsLoading,
    error: groupsError,
  } = useFindGroups(userId, { authReady });

  const { upcomingGroups, selectedGroupId, setSelectedGroupId, selectedGroup } =
    useFindSelectedGroup(userId, groups);

  useEffect(() => {
    registerFindRemoteLogLifecycle();
  }, []);

  useEffect(() => {
    if (!authReady || !userId) {
      return;
    }
    void ensureFindRemoteLogPageContext({ userId, groupId: selectedGroupId });
  }, [authReady, userId, selectedGroupId]);

  useEffect(() => {
    if (authReady && !userId) {
      void shutdownFindRemoteLogPageContext();
    }
  }, [authReady, userId]);

  // Which event the session is for, decided by what is ON, not by a picked group (Sam, 2026-10-12: nobody should have to
  // choose a group on Find or the map - everyone in any group you share for the event, plus the people you follow back
  // going to it, see each other). In order:
  //   1. a live event you have a group for, or one you are going to with no group (solo: you publish, mutual follows
  //      are watched - 141) - of those, the one on now (pickCurrentLiveEvent: the start that most recently passed, not
  //      the earliest, which kept a day party's session all through the night show - 2026-10-16 audit). Your group
  //      for the same event (or festival) always beats your RSVP to it, and wins a tie;
  //   2. on Find or the map with nothing live, a QA group (qa_bypass_presence) - testing from a desk any day.
  // Never otherwise: on a day with nothing happening Prii uses no location at all - not browsing, not on Find or a map,
  // no blue arrow (Sam, 2026-10-12; the preview used to run for any next group, which put the arrow up while browsing).
  // A group whose event is over never runs (Sam, 2026-09-28).
  const {
    event: liveRsvpEvent,
    next: nextRsvp,
    loading: liveRsvpLoading,
  } = useLiveRsvpEvent(userId, {
    authReady,
  });
  const now = Date.now();
  const bestLiveGroup = pickCurrentLiveEvent(
    upcomingGroups.filter((g) => isEventLiveNow(g, now)),
    {
      startMs: getEventStartMs,
      id: (g) => String(g.id),
      now,
    },
  );
  const soloWins =
    Boolean(bestLiveGroup && liveRsvpEvent) &&
    (bestLiveGroup.eventRootId ?? bestLiveGroup.eventId) !==
      (liveRsvpEvent.festival_parent_event_id ?? liveRsvpEvent.event_id) &&
    pickCurrentLiveEvent([bestLiveGroup, liveRsvpEvent], {
      startMs: getEventStartMs,
      id: (c) => (c === bestLiveGroup ? '0' : '1'), // a tie goes to the group
      now,
    }) === liveRsvpEvent;
  const liveGroup = soloWins ? null : bestLiveGroup;
  const previewGroup =
    !bestLiveGroup && !liveRsvpEvent && findPageActive
      ? (upcomingGroups.find((g) => g.qaBypassPresence) ?? null)
      : null;
  const sessionGroup = liveGroup ?? previewGroup;
  const groupRuns = Boolean(sessionGroup?.id);
  const solo = !groupRuns && Boolean(liveRsvpEvent);
  const sessionEventId = groupRuns
    ? (sessionGroup.eventId ?? null)
    : solo
      ? (liveRsvpEvent?.event_id ?? null)
      : null;
  const sessionEventRootId = groupRuns
    ? (sessionGroup.eventRootId ?? sessionGroup.eventId ?? null)
    : solo
      ? (liveRsvpEvent?.festival_parent_event_id ?? liveRsvpEvent?.event_id ?? null)
      : null;
  // All of your groups for that event: their members are all watched. The first is the session's primary group (the
  // native publisher's slot and the run key), so it only changes if you leave it.
  const sessionGroupIds = useMemo(
    () =>
      sessionEventRootId
        ? upcomingGroups
            .filter((g) => (g.eventRootId ?? g.eventId) === sessionEventRootId)
            .map((g) => g.id)
        : [],
    [upcomingGroups, sessionEventRootId],
  );
  // Pinned while you stay in it: the run key is userId:groupId, so letting the sort pick a new "first" group (a second
  // group for tonight with a lower id, or one on the festival parent) restarted the whole session (2026-10-12 audit).
  const primaryRef = useRef(null);
  const sessionGroupId = !groupRuns
    ? null
    : primaryRef.current && sessionGroupIds.includes(primaryRef.current)
      ? primaryRef.current
      : (sessionGroupIds[0] ?? sessionGroup.id);
  primaryRef.current = sessionGroupId;
  // When the session's event stops being live. Swift stops itself there: in the background JS is suspended and the
  // check above never runs, so a phone kept sharing all night after the event (2026-10-07 QA). A preview session (Find
  // or the map open, nothing live) has no end - JS stops it when you leave the page.
  const sessionLiveUntilMs = liveGroup
    ? (getFindLiveUntil({
        ends_at: liveGroup.eventEndsAt,
        event_date: liveGroup.eventDate,
        is_festival: liveGroup.eventIsFestival,
      })?.getTime() ?? null)
    : solo
      ? (getFindLiveUntil(liveRsvpEvent)?.getTime() ?? null)
      : null;
  // Where the event is, for the native publisher (2026-10-12): off site nobody can see you (RLS shows a position only
  // within event_presence_radius_m - 5 km since 147 - or inside the map), so it publishes nothing there, and
  // once you have been there and gone 45 min it stops. Not for a preview session (QA from home) or a QA-bypass group.
  const venueOf = (lat, lng) =>
    Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng, radiusM: PRESENCE_RADIUS_M } : null;
  const sessionVenue =
    liveGroup && !upcomingGroups.some((g) => sessionGroupIds.includes(g.id) && g.qaBypassPresence)
      ? venueOf(liveGroup.eventVenueLat, liveGroup.eventVenueLng)
      : solo
        ? venueOf(
            Number(liveRsvpEvent?.venues?.latitude ?? NaN),
            Number(liveRsvpEvent?.venues?.longitude ?? NaN),
          )
        : null;
  const proximity = useProximity({
    userId,
    groupId: sessionGroupId,
    groupIds: sessionGroupIds,
    solo,
    eventId: sessionEventId,
    liveUntilMs: sessionLiveUntilMs,
    venue: sessionVenue,
    enabled: authReady && Boolean(userId) && (groupRuns || solo),
    selfProfile: profile,
    findPageActive,
  });

  // No automatic "Always" ask when an event goes live (operator, 2026-10-04: Prii asks for While Using only; the
  // 2026-10-04 morning auto-ask is reverted).

  const value = useMemo(
    () => ({
      ...proximity,
      findPageActive,
      solo,
      soloEvent: liveRsvpEvent,
      nextRsvp,
      sessionLoading: groupsLoading || liveRsvpLoading,
      sessionEventId,
      sessionEventRootId,
      sessionGroupIds,
      upcomingGroups,
      selectedGroupId,
      setSelectedGroupId,
      selectedGroup,
      groups,
      groupsLoading,
      groupsError,
    }),
    [
      proximity,
      findPageActive,
      solo,
      liveRsvpEvent,
      nextRsvp,
      liveRsvpLoading,
      sessionEventId,
      sessionEventRootId,
      sessionGroupIds,
      upcomingGroups,
      selectedGroupId,
      setSelectedGroupId,
      selectedGroup,
      groups,
      groupsLoading,
      groupsError,
    ],
  );

  return <FindProximityContext.Provider value={value}>{children}</FindProximityContext.Provider>;
}

export function useFindProximity() {
  const context = useContext(FindProximityContext);
  if (!context) {
    throw new Error('useFindProximity must be used within FindProximityProvider');
  }
  return context;
}
